import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faBookOpen } from '@fortawesome/free-solid-svg-icons';
import { PLATFORM_ROUTES } from '@/routes/platform-routes';
import {
  Callout,
  Checklist,
  CodeBlock,
  FigureFrame,
  GuideToc,
  Mono,
  Section,
  Table,
  useTocScrollSpy,
} from './components/guide-primitives';

/**
 * Cloudflare R2 Storage Provider — Setup & Migration Guide.
 *
 * Opened in a NEW TAB from the Storage Providers page so the operator keeps the
 * Configure form open in the original tab.
 *
 * EVERYTHING HERE IS DERIVED FROM THE IMPLEMENTATION:
 *
 * - The FOUR configuration fields come from `PROVIDER_CONFIG_SCHEMA.r2`:
 *   accountId, bucket, accessKeyId, secretAccessKey. R2 has NO region field and
 *   NO endpoint field — unlike the S3 provider — because `R2StorageDriver` sets
 *   `defaultRegion = 'auto'` and derives the endpoint from the account id.
 * - The operations are the seven AWS SDK commands the shared
 *   `S3CompatibleStorageDriver` issues. R2 uses Cloudflare's own token
 *   permission model, NOT an AWS IAM JSON policy, so no IAM policy is shown.
 * - The connection test is `HeadBucketCommand` only — documented as such,
 *   including what it therefore does NOT prove.
 *
 * If a field, permission or behaviour is not in the product, it is not here.
 */

const R2_FIGURE_DISCLAIMER =
  'Illustrative diagram, not a live Cloudflare screenshot. Cloudflare changes its ' +
  'dashboard wording and layout frequently — follow the navigation path described in the ' +
  'steps, and use this diagram to confirm you are on the right screen.';

function Figure(props: { n: number; caption: string; look: string; children: React.ReactNode }) {
  return <FigureFrame {...props} disclaimer={R2_FIGURE_DISCLAIMER} />;
}

const ENDPOINT_TEMPLATE = `https://YOUR_ACCOUNT_ID.r2.cloudflarestorage.com`;

const DERIVED_CONFIG = `Region    : auto                                    (forced by the driver — no form field)
Endpoint  : https://YOUR_ACCOUNT_ID.r2.cloudflarestorage.com   (derived from Account ID)
Addressing: path-style                             (an endpoint is present, so forcePathStyle = true)
Object key: tenant/<tenantId>/<module>/<entity>/<class>/<uuid>.<ext>`;

const TOC = [
  { id: 'before-you-start', label: 'Before you start' },
  { id: 'app-config', label: 'The application configuration' },
  { id: 'enable-r2', label: 'Enable R2' },
  { id: 'create-bucket', label: 'Create the bucket' },
  { id: 'credentials-kinds', label: 'Which credentials' },
  { id: 'permissions', label: 'Permissions required' },
  { id: 'generate', label: 'Generate credentials' },
  { id: 'endpoint', label: 'The S3 endpoint' },
  { id: 'configure', label: 'Configure the provider' },
  { id: 'connection-test', label: 'Connection test' },
  { id: 'verify-ops', label: 'Verify real operations' },
  { id: 'migration', label: 'R2 and storage migration' },
  { id: 'readiness', label: 'Migration readiness' },
  { id: 'troubleshooting', label: 'Troubleshooting' },
  { id: 'security', label: 'Security best practices' },
  { id: 'rotation', label: 'Credential rotation' },
  { id: 'operational', label: 'Operational notes' },
  { id: 'quick-start', label: 'Quick start checklist' },
] as const;

const TOC_IDS = TOC.map((t) => t.id);

export function CloudflareR2SetupGuidePage() {
  const active = useTocScrollSpy(TOC_IDS, TOC[0].id);

  return (
    <div className="mx-auto max-w-[1200px] px-4 py-6">
      <header className="mb-6">
        <Link
          to={PLATFORM_ROUTES.STORAGE_PROVIDERS}
          className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
          Back to Storage Providers
        </Link>
        <div className="flex items-start gap-3">
          <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <FontAwesomeIcon icon={faBookOpen} className="h-5 w-5 text-primary" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              Cloudflare R2 Storage Provider — Setup &amp; Migration Guide
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              A complete, step-by-step operational guide for creating a Cloudflare R2 bucket,
              issuing the right credentials, and connecting it to Service Portal. Written for
              administrators who have never configured R2 before.
            </p>
          </div>
        </div>

        <Callout tone="tip" title="Working across two tabs">
          <p>
            This guide opened in a new tab. Your <strong>Configure Provider</strong> form is still
            open, untouched, in the original tab — switch back to it at any time and nothing you
            typed will be lost.
          </p>
        </Callout>

        <Callout tone="warning" title="R2 is configured differently from Amazon S3">
          <p>
            If you have already set up the S3 provider, do not assume the same form. The R2 provider
            asks for <strong>four</strong> fields — <Mono>Account ID</Mono>,{' '}
            <Mono>Bucket Name</Mono>, <Mono>Access Key ID</Mono>, <Mono>Secret Access Key</Mono> —
            and has <strong>no Region field and no Custom Endpoint field</strong>. The driver
            supplies both itself. Section 2 explains exactly why.
          </p>
        </Callout>
      </header>

      <div className="gap-8 lg:grid lg:grid-cols-[240px_minmax(0,1fr)]">
        <GuideToc items={TOC} active={active} />

        <main className="min-w-0 space-y-8">
          {/* 1 */}
          <section id="before-you-start" className="scroll-mt-24">
            <h2 className="mb-4 text-xl font-bold text-foreground">
              <span className="mr-2 text-primary">1.</span>Before you start
            </h2>
            <div className="space-y-3 text-sm leading-relaxed text-foreground">
              <p>
                Cloudflare R2 is S3-compatible object storage. Service Portal talks to it through
                the same driver family it uses for Amazon S3, over R2&rsquo;s S3-compatible API — so
                everything here is about producing four values and pasting them into one form.
              </p>

              <Table
                head={['You need', 'Why', 'Where']}
                rows={[
                  [
                    'A Cloudflare account with R2 enabled',
                    'R2 requires a payment method on file before buckets can be created, even within the free allowance',
                    <Mono key="a">dash.cloudflare.com</Mono>,
                  ],
                  [
                    'Permission to create R2 buckets and API tokens in that account',
                    'You will create one bucket and one R2 API token',
                    'Cloudflare account role',
                  ],
                  [
                    <span key="b">
                      Service Portal <Mono>platform:system:manage</Mono>
                    </span>,
                    'Required to configure a provider. Viewing is platform:system:view',
                    'Platform Console → your role',
                  ],
                  [
                    'A secret manager',
                    'The R2 secret access key is displayed once and never again',
                    'Your organisation’s vault',
                  ],
                ]}
              />

              <h3 className="mt-5 text-base font-semibold text-foreground">
                Have these four values ready
              </h3>
              <p>
                By the end of section 7 you will hold exactly these, and nothing else is needed:
              </p>
              <Table
                head={['Value', 'Looks like', 'From']}
                rows={[
                  [
                    <strong key="1">Account ID</strong>,
                    <Mono key="a">YOUR_ACCOUNT_ID</Mono>,
                    'R2 dashboard / account home',
                  ],
                  [
                    <strong key="2">Bucket name</strong>,
                    <Mono key="b">YOUR_BUCKET_NAME</Mono>,
                    'The bucket you create in section 4',
                  ],
                  [
                    <strong key="3">Access Key ID</strong>,
                    <Mono key="c">YOUR_ACCESS_KEY_ID</Mono>,
                    'R2 API token (section 7)',
                  ],
                  [
                    <strong key="4">Secret Access Key</strong>,
                    <Mono key="d">YOUR_SECRET_ACCESS_KEY</Mono>,
                    'R2 API token — shown once',
                  ],
                ]}
              />

              <Callout tone="security" title="Before you begin">
                <ul className="ml-4 list-disc space-y-1">
                  <li>
                    Create a token dedicated to this environment. Never reuse one across prod and
                    staging.
                  </li>
                  <li>
                    Never put these values in source code, in frontend code, or in a committed{' '}
                    <Mono>.env</Mono>.
                  </li>
                  <li>Never paste the secret into a ticket, chat, screenshot or log.</li>
                  <li>
                    R2 buckets are private by default. Nothing in this guide asks you to make one
                    public, and you should not.
                  </li>
                </ul>
              </Callout>

              <Figure
                n={1}
                caption="Cloudflare account → R2 bucket → Service Portal"
                look="Four values cross the boundary, and only four. The endpoint is not one of them — the driver builds it from the Account ID."
              >
                <svg
                  viewBox="0 0 880 250"
                  className="h-auto w-full min-w-[680px]"
                  role="img"
                  aria-label="Diagram: a Cloudflare account contains an R2 bucket and an R2 API token; the account id, bucket name, access key id and secret access key are entered into the Service Portal provider form, which derives the endpoint."
                >
                  <rect
                    x="10"
                    y="20"
                    width="250"
                    height="210"
                    rx="8"
                    className="fill-background stroke-border"
                    strokeWidth="1.5"
                  />
                  <text
                    x="135"
                    y="44"
                    textAnchor="middle"
                    className="fill-foreground text-[12px] font-semibold"
                  >
                    Cloudflare account
                  </text>
                  <text
                    x="135"
                    y="62"
                    textAnchor="middle"
                    className="fill-muted-foreground font-mono text-[10px]"
                  >
                    YOUR_ACCOUNT_ID
                  </text>
                  <rect
                    x="30"
                    y="80"
                    width="210"
                    height="52"
                    rx="6"
                    className="fill-primary/10 stroke-primary"
                    strokeWidth="1.2"
                  />
                  <text x="135" y="100" textAnchor="middle" className="fill-foreground text-[11px]">
                    R2 bucket
                  </text>
                  <text
                    x="135"
                    y="118"
                    textAnchor="middle"
                    className="fill-muted-foreground font-mono text-[10px]"
                  >
                    YOUR_BUCKET_NAME
                  </text>
                  <rect
                    x="30"
                    y="146"
                    width="210"
                    height="66"
                    rx="6"
                    className="fill-muted/40 stroke-border"
                    strokeWidth="1.2"
                  />
                  <text x="135" y="166" textAnchor="middle" className="fill-foreground text-[11px]">
                    R2 API token
                  </text>
                  <text
                    x="135"
                    y="184"
                    textAnchor="middle"
                    className="fill-muted-foreground font-mono text-[9px]"
                  >
                    YOUR_ACCESS_KEY_ID
                  </text>
                  <text
                    x="135"
                    y="200"
                    textAnchor="middle"
                    className="fill-muted-foreground font-mono text-[9px]"
                  >
                    YOUR_SECRET_ACCESS_KEY
                  </text>

                  <line
                    x1="262"
                    y1="125"
                    x2="330"
                    y2="125"
                    className="stroke-muted-foreground"
                    strokeWidth="1.5"
                    markerEnd="url(#r2arrow)"
                  />
                  <text
                    x="296"
                    y="117"
                    textAnchor="middle"
                    className="fill-muted-foreground text-[9px]"
                  >
                    4 values
                  </text>

                  <rect
                    x="336"
                    y="20"
                    width="250"
                    height="210"
                    rx="8"
                    className="fill-background stroke-border"
                    strokeWidth="1.5"
                  />
                  <text
                    x="461"
                    y="44"
                    textAnchor="middle"
                    className="fill-foreground text-[12px] font-semibold"
                  >
                    Service Portal form
                  </text>
                  {['Account ID', 'Bucket Name', 'Access Key ID', 'Secret Access Key'].map(
                    (f, i) => (
                      <g key={f}>
                        <rect
                          x="356"
                          y={64 + i * 40}
                          width="210"
                          height="28"
                          rx="4"
                          className="fill-muted/40 stroke-border"
                          strokeWidth="1"
                        />
                        <text x="366" y={82 + i * 40} className="fill-foreground text-[10px]">
                          {f}
                        </text>
                      </g>
                    ),
                  )}

                  <line
                    x1="588"
                    y1="125"
                    x2="656"
                    y2="125"
                    className="stroke-muted-foreground"
                    strokeWidth="1.5"
                    markerEnd="url(#r2arrow)"
                  />
                  <text
                    x="622"
                    y="117"
                    textAnchor="middle"
                    className="fill-muted-foreground text-[9px]"
                  >
                    derives
                  </text>

                  <rect
                    x="662"
                    y="60"
                    width="208"
                    height="130"
                    rx="8"
                    className="fill-primary/5 stroke-primary/40"
                    strokeWidth="1.5"
                  />
                  <text
                    x="766"
                    y="84"
                    textAnchor="middle"
                    className="fill-foreground text-[11px] font-semibold"
                  >
                    r2 driver
                  </text>
                  <text x="672" y="108" className="fill-muted-foreground font-mono text-[9px]">
                    region = &quot;auto&quot;
                  </text>
                  <text x="672" y="126" className="fill-muted-foreground font-mono text-[8px]">
                    endpoint =
                  </text>
                  <text x="672" y="140" className="fill-muted-foreground font-mono text-[8px]">
                    https://&lt;acct&gt;.r2.
                  </text>
                  <text x="672" y="152" className="fill-muted-foreground font-mono text-[8px]">
                    cloudflarestorage.com
                  </text>
                  <text x="672" y="172" className="fill-muted-foreground font-mono text-[9px]">
                    path-style = true
                  </text>

                  <defs>
                    <marker
                      id="r2arrow"
                      markerWidth="8"
                      markerHeight="8"
                      refX="7"
                      refY="4"
                      orient="auto"
                    >
                      <path d="M0,0 L8,4 L0,8 z" className="fill-muted-foreground" />
                    </marker>
                  </defs>
                </svg>
              </Figure>
            </div>
          </section>

          {/* 2 */}
          <Section id="app-config" n={2} title="The application configuration">
            <p>
              These are the fields the R2 provider actually has. There are four, all required, and
              nothing else is offered.
            </p>
            <Table
              head={['Field (form label)', 'Config key', 'Required', 'Stored', 'Maps to']}
              rows={[
                [
                  <strong key="1">Account ID</strong>,
                  <Mono key="k1">accountId</Mono>,
                  <span key="r1" className="font-semibold text-primary">
                    Yes
                  </span>,
                  'Plain text',
                  'Your Cloudflare account identifier',
                ],
                [
                  <strong key="2">Bucket Name</strong>,
                  <Mono key="k2">bucket</Mono>,
                  <span key="r2" className="font-semibold text-primary">
                    Yes
                  </span>,
                  'Plain text',
                  'The R2 bucket name, exactly as created',
                ],
                [
                  <strong key="3">Access Key ID</strong>,
                  <Mono key="k3">accessKeyId</Mono>,
                  <span key="r3" className="font-semibold text-primary">
                    Yes
                  </span>,
                  <strong key="s3">Encrypted at rest</strong>,
                  'R2 API token → Access Key ID',
                ],
                [
                  <strong key="4">Secret Access Key</strong>,
                  <Mono key="k4">secretAccessKey</Mono>,
                  <span key="r4" className="font-semibold text-primary">
                    Yes
                  </span>,
                  <strong key="s4">Encrypted at rest</strong>,
                  'R2 API token → Secret Access Key',
                ],
              ]}
            />

            <Callout tone="note" title="There is no Region field and no Custom Endpoint field">
              <p>
                This is the single biggest difference from the Amazon S3 provider, and it is
                deliberate. The driver fills both in for you:
              </p>
              <ul className="ml-4 list-disc space-y-1">
                <li>
                  <strong>Region</strong> is forced to the literal <Mono>auto</Mono>. R2 mandates
                  that exact value, so asking an operator for it would be a formality with only one
                  correct answer — and a chance to get it wrong.
                </li>
                <li>
                  <strong>Endpoint</strong> is derived as{' '}
                  <Mono>https://&lt;accountId&gt;.r2.cloudflarestorage.com</Mono>. It is fully
                  determined by the account, which is why the form asks for the Account ID instead.
                </li>
              </ul>
              <p>
                So: if you were expecting to type a region or an endpoint, you will not see those
                boxes, and nothing is missing.
              </p>
            </Callout>

            <p>What the driver computes once your four values are saved:</p>
            <CodeBlock label="derived at runtime — nothing to enter" code={DERIVED_CONFIG} />

            <Callout tone="note" title="Object keys are generated by the application">
              <p>
                There is no prefix, folder or path setting. Service Portal builds its own keys —{' '}
                <Mono>tenant/&lt;tenantId&gt;/&lt;module&gt;/…</Mono> — and writes them at the root
                of the bucket. Use a bucket dedicated to Service Portal rather than trying to fence
                it into a folder of a shared one.
              </p>
            </Callout>
          </Section>

          {/* 3 */}
          <Section id="enable-r2" n={3} title="Enable R2">
            <ol className="ml-5 list-decimal space-y-2">
              <li>
                Sign in at <Mono>https://dash.cloudflare.com/</Mono>.
              </li>
              <li>
                Select your account, then choose <strong>R2</strong> in the left-hand navigation.
                Depending on the dashboard version it may appear as{' '}
                <strong>R2 Object Storage</strong>.
              </li>
              <li>
                If R2 has never been used on this account you will be asked to enable it and to add
                a <strong>payment method</strong>. This is required even if you expect to stay
                inside the free allowance; no bucket can be created until it is done.
              </li>
              <li>
                Once enabled you land on the R2 overview, which lists buckets and gives access to
                <strong> Manage R2 API Tokens</strong>.
              </li>
            </ol>
            <Callout tone="tip">
              <p>
                Note your <strong>Account ID</strong> while you are here — the R2 overview page
                displays it, and it is the value the provider form needs. It is also the subdomain
                of your S3 endpoint (section 8).
              </p>
            </Callout>
          </Section>

          {/* 4 */}
          <Section id="create-bucket" n={4} title="Create the bucket">
            <ol className="ml-5 list-decimal space-y-2">
              <li>
                R2 → <strong>Create bucket</strong>.
              </li>
              <li>
                <strong>Bucket name</strong> — lowercase letters, numbers and hyphens; 3–63
                characters; unique within your account. Put the environment in the name, e.g.{' '}
                <Mono>acme-serviceportal-prod</Mono>.
              </li>
              <li>
                <strong>Location</strong> — choose an automatic/hint location close to your backend,
                or a specific jurisdiction if data residency requires it. R2 has no
                &ldquo;region&rdquo; in the S3 sense; this does not change what you type into
                Service Portal, which always uses <Mono>auto</Mono>.
              </li>
              <li>
                <strong>Default storage class</strong> — leave at <strong>Standard</strong>. The
                driver sends no storage-class parameter, and Infrequent Access adds retrieval costs
                and minimum-duration charges that do not suit an active asset store.
              </li>
              <li>
                <strong>Create bucket</strong>, then confirm it appears in the bucket list.
              </li>
            </ol>

            <h3 className="mt-5 text-base font-semibold text-foreground">What NOT to configure</h3>
            <Table
              head={['Setting', 'Do', 'Why']}
              rows={[
                [
                  'Public access / r2.dev public URL',
                  <strong key="a">Leave disabled</strong>,
                  'Service Portal serves files through time-limited signed URLs it generates itself. It never needs public objects, and enabling public access would expose customer files.',
                ],
                [
                  'Custom domain',
                  <strong key="b">Not required</strong>,
                  'The driver addresses the S3 endpoint directly. A custom domain is for public delivery, which this application does not use.',
                ],
                [
                  'Object lifecycle rules',
                  <strong key="c">None</strong>,
                  'Service Portal has its own asset lifecycle with quarantine and retention. An R2-side expiry rule can delete objects the catalogue still considers live, which later surfaces as a missing object during a migration gate check.',
                ],
                [
                  'CORS policy',
                  <strong key="d">Not required</strong>,
                  'Browsers are sent signed URLs and fetch objects by navigation/download, not by cross-origin XHR from the console. Add CORS only if you later build a direct browser-to-R2 flow, which this application does not have.',
                ],
                [
                  'Bucket sharding / jurisdiction changes after creation',
                  <strong key="e">Decide up front</strong>,
                  'Location cannot be changed later; you would have to create a new bucket and migrate.',
                ],
              ]}
            />
            <Callout tone="warning" title="Do not enable a public bucket to solve an access error">
              <p>
                If something returns AccessDenied, the cause is the token&rsquo;s permissions or its
                bucket scope — see sections 6 and 14. Making the bucket public does not fix it and
                exposes every stored file.
              </p>
            </Callout>
          </Section>

          {/* 5 */}
          <Section id="credentials-kinds" n={5} title="Which credentials — this trips people up">
            <p>
              Cloudflare offers several different credential types with confusingly similar names.
              Only one of them works here.
            </p>
            <Table
              head={['Credential type', 'What it is', 'Use for Service Portal?']}
              rows={[
                [
                  <strong key="1">R2 API token (S3-compatible)</strong>,
                  'Issued from R2 → Manage R2 API Tokens. Produces an Access Key ID and a Secret Access Key.',
                  <span key="y" className="font-semibold text-primary">
                    YES — this is the only one that works
                  </span>,
                ],
                [
                  'Cloudflare API token',
                  'A general-purpose scoped token for the Cloudflare REST API. A single bearer value.',
                  <span key="n1" className="font-semibold text-destructive">
                    No
                  </span>,
                ],
                [
                  'Global API key',
                  'A legacy account-wide key tied to your login email, with full account access.',
                  <span key="n2" className="font-semibold text-destructive">
                    No — and never paste it anywhere
                  </span>,
                ],
                [
                  'Account API token for Workers/DNS/etc.',
                  'Scoped to other Cloudflare products.',
                  <span key="n3" className="font-semibold text-destructive">
                    No
                  </span>,
                ],
              ]}
            />
            <Callout tone="warning" title="The distinguishing test">
              <p>
                Service Portal signs requests with <strong>AWS Signature V4</strong>, which requires
                an <strong>access key ID / secret access key pair</strong>. If what you are holding
                is a single long bearer token, it is the wrong kind of credential and no amount of
                pasting will make it work. Go back to <strong>R2 → Manage R2 API Tokens</strong>.
              </p>
            </Callout>
          </Section>

          {/* 6 */}
          <Section id="permissions" n={6} title="Permissions required">
            <Callout tone="note" title="R2 does not use AWS IAM JSON policies">
              <p>
                If you configured the Amazon S3 provider, you pasted a JSON policy document. R2 does
                not work that way: permissions are chosen from a small set of{' '}
                <strong>token permission levels</strong>, and the token is scoped to specific
                buckets. There is no JSON to paste here, and copying the AWS policy would be
                meaningless.
              </p>
            </Callout>

            <h3 className="text-base font-semibold text-foreground">
              What the application actually does
            </h3>
            <p>
              Derived from the S3 SDK commands the driver issues — this is the complete list, with
              nothing added for migration:
            </p>
            <Table
              head={['Driver method', 'SDK command', 'Needs']}
              rows={[
                [<Mono key="1">upload()</Mono>, 'PutObjectCommand', 'write objects'],
                [
                  <Mono key="2">download() / getChecksum()</Mono>,
                  'GetObjectCommand',
                  'read objects',
                ],
                [<Mono key="3">exists()</Mono>, 'HeadObjectCommand', 'read object metadata'],
                [<Mono key="4">delete()</Mono>, 'DeleteObjectCommand', 'delete objects'],
                [
                  <Mono key="5">copyWithinProvider()</Mono>,
                  'CopyObjectCommand',
                  'read + write objects',
                ],
                [
                  <Mono key="6">getUsageStats() / listObjects()</Mono>,
                  'ListObjectsV2Command',
                  'list objects in the bucket',
                ],
                [<Mono key="7">testConnection()</Mono>, 'HeadBucketCommand', 'address the bucket'],
                [
                  <Mono key="8">getSignedUrl()</Mono>,
                  'presigned GetObjectCommand',
                  'nothing at signing time — signing is local; the read happens when the URL is opened',
                ],
              ]}
            />

            <h3 className="mt-5 text-base font-semibold text-foreground">Choose this level</h3>
            <Table
              head={['R2 token permission', 'Grants', 'Use it?']}
              rows={[
                [
                  <strong key="1">Object Read &amp; Write</strong>,
                  'Read, write, delete and list objects in the buckets you scope the token to',
                  <span key="y" className="font-semibold text-primary">
                    YES — this is the correct choice
                  </span>,
                ],
                [
                  'Object Read only',
                  'Read and list objects',
                  <span key="n1" className="font-semibold text-destructive">
                    No — uploads and deletes would fail
                  </span>,
                ],
                [
                  'Admin Read &amp; Write',
                  'Everything above PLUS creating, configuring and deleting buckets',
                  <span key="n2" className="font-semibold text-destructive">
                    No — more than is needed
                  </span>,
                ],
                [
                  'Admin Read only',
                  'List buckets and read configuration',
                  <span key="n3" className="font-semibold text-destructive">
                    No
                  </span>,
                ],
              ]}
            />

            <Callout tone="security" title="Least privilege — two rules">
              <p>
                <strong>1. Object Read &amp; Write, not Admin.</strong> The application never
                creates, reconfigures or deletes a bucket — it only works with objects inside one
                that already exists. An Admin token would let a compromised credential destroy the
                bucket itself.
              </p>
              <p>
                <strong>2. Scope the token to the one bucket.</strong> When Cloudflare offers
                &ldquo;Apply to specific buckets&rdquo;, select only your Service Portal bucket.
                Never issue an account-wide R2 token for this.
              </p>
            </Callout>

            <Table
              head={['NOT required — do not grant', 'Why not']}
              rows={[
                ['Bucket create / delete', 'The application never creates or removes a bucket'],
                ['Bucket configuration / lifecycle management', 'Never called by the driver'],
                ['Public bucket / r2.dev management', 'Objects are served by signed URL only'],
                [
                  'Any Cloudflare permission outside R2 (DNS, Workers, Zone, Account settings)',
                  'Entirely unrelated to storage',
                ],
                [
                  'Object version APIs',
                  'The driver never sends a VersionId and never lists versions — it is version-unaware',
                ],
              ]}
            />

            <Figure
              n={2}
              caption="Credential mapping — R2 token to provider form"
              look="The token yields exactly two secret values, and each maps to one field. The token id and the token name are not entered anywhere."
            >
              <svg
                viewBox="0 0 820 190"
                className="h-auto w-full min-w-[640px]"
                role="img"
                aria-label="Diagram mapping the R2 API token's Access Key ID to the provider's Access Key ID field and the Secret Access Key to the Secret Access Key field."
              >
                <rect
                  x="10"
                  y="20"
                  width="330"
                  height="150"
                  rx="8"
                  className="fill-background stroke-border"
                  strokeWidth="1.5"
                />
                <text
                  x="175"
                  y="44"
                  textAnchor="middle"
                  className="fill-foreground text-[12px] font-semibold"
                >
                  R2 API token (S3-compatible)
                </text>
                {[
                  ['Token name', 'service-portal-prod', false],
                  ['Access Key ID', 'YOUR_ACCESS_KEY_ID', true],
                  ['Secret Access Key', 'YOUR_SECRET_ACCESS_KEY', true],
                ].map(([l, v, use], i) => (
                  <g key={String(l)}>
                    <text x="30" y={72 + i * 34} className="fill-muted-foreground text-[10px]">
                      {String(l)}
                    </text>
                    <rect
                      x="140"
                      y={58 + i * 34}
                      width="180"
                      height="22"
                      rx="4"
                      className={
                        use ? 'fill-primary/10 stroke-primary' : 'fill-muted/30 stroke-border'
                      }
                      strokeWidth="1"
                    />
                    <text x="150" y={73 + i * 34} className="fill-foreground font-mono text-[8px]">
                      {String(v)}
                    </text>
                  </g>
                ))}
                <line
                  x1="342"
                  y1="95"
                  x2="410"
                  y2="95"
                  className="stroke-muted-foreground"
                  strokeWidth="1.5"
                  markerEnd="url(#mapArrow)"
                />
                <rect
                  x="416"
                  y="20"
                  width="394"
                  height="150"
                  rx="8"
                  className="fill-background stroke-border"
                  strokeWidth="1.5"
                />
                <text
                  x="613"
                  y="44"
                  textAnchor="middle"
                  className="fill-foreground text-[12px] font-semibold"
                >
                  Service Portal · Configure Cloudflare R2
                </text>
                {['Account ID', 'Bucket Name', 'Access Key ID', 'Secret Access Key'].map((f, i) => (
                  <g key={f}>
                    <text x="436" y={70 + i * 26} className="fill-muted-foreground text-[10px]">
                      {f}
                    </text>
                    <rect
                      x="560"
                      y={57 + i * 26}
                      width="230"
                      height="19"
                      rx="3"
                      className={
                        i >= 2 ? 'fill-primary/10 stroke-primary' : 'fill-muted/30 stroke-border'
                      }
                      strokeWidth="1"
                    />
                  </g>
                ))}
                <defs>
                  <marker
                    id="mapArrow"
                    markerWidth="8"
                    markerHeight="8"
                    refX="7"
                    refY="4"
                    orient="auto"
                  >
                    <path d="M0,0 L8,4 L0,8 z" className="fill-muted-foreground" />
                  </marker>
                </defs>
              </svg>
            </Figure>
          </Section>

          {/* 7 */}
          <Section id="generate" n={7} title="Generate credentials">
            <ol className="ml-5 list-decimal space-y-2">
              <li>
                R2 → <strong>Manage R2 API Tokens</strong> (on the R2 overview page, often top-right
                or in the R2 side navigation).
              </li>
              <li>
                <strong>Create API token</strong>.
              </li>
              <li>
                Name it for its job and environment, e.g. <Mono>service-portal-prod</Mono>.
              </li>
              <li>
                Permission: <strong>Object Read &amp; Write</strong> (section 6).
              </li>
              <li>
                Scope: choose <strong>Apply to specific buckets</strong> and select only your
                Service Portal bucket.
              </li>
              <li>
                Optionally set a TTL. If you do, diarise the expiry — an expired token takes storage
                offline (section 16 covers rotation).
              </li>
              <li>
                <strong>Create API token</strong>.
              </li>
              <li>
                Cloudflare now shows the S3-compatible credentials. Copy{' '}
                <strong>Access Key ID</strong> and <strong>Secret Access Key</strong>.
              </li>
            </ol>

            <Callout tone="warning" title="The secret is shown exactly once">
              <p>
                Leave that screen and the Secret Access Key cannot be retrieved — not by you, not by
                Cloudflare support. Copy it into your secret manager, or straight into the Service
                Portal form, before navigating away.
              </p>
              <p>
                <strong>If it is lost:</strong> you cannot recover it. Create a new token, update
                Service Portal, run a connection test, verify, and only then delete the old token.
              </p>
            </Callout>

            <Callout tone="note">
              <p>
                The same screen usually also shows a <strong>jurisdiction-specific endpoint</strong>{' '}
                for your account. That is the value section 8 describes — you do not enter it, but
                it is a useful cross-check that your Account ID is the one you think it is.
              </p>
            </Callout>

            <Figure
              n={3}
              caption="R2 → Manage R2 API Tokens → token created"
              look="Two values, and a warning that the secret will not be shown again. The permission should read Object Read & Write, scoped to your bucket."
            >
              <svg
                viewBox="0 0 760 210"
                className="h-auto w-full min-w-[600px]"
                role="img"
                aria-label="Wireframe of the Cloudflare R2 API token result screen showing Access Key ID and a masked Secret Access Key."
              >
                <rect
                  x="10"
                  y="10"
                  width="740"
                  height="190"
                  rx="6"
                  className="fill-background stroke-border"
                  strokeWidth="1.5"
                />
                <text x="30" y="36" className="fill-foreground text-[13px] font-semibold">
                  API token created
                </text>
                <text x="30" y="58" className="fill-muted-foreground text-[10px]">
                  Permission: Object Read &amp; Write · Buckets: YOUR_BUCKET_NAME
                </text>
                <text x="30" y="86" className="fill-muted-foreground text-[10px]">
                  Access Key ID
                </text>
                <rect
                  x="30"
                  y="94"
                  width="330"
                  height="24"
                  rx="4"
                  className="fill-muted/40 stroke-border"
                  strokeWidth="1"
                />
                <text x="40" y="110" className="fill-foreground font-mono text-[10px]">
                  YOUR_ACCESS_KEY_ID
                </text>
                <text x="30" y="142" className="fill-muted-foreground text-[10px]">
                  Secret Access Key
                </text>
                <rect
                  x="30"
                  y="150"
                  width="330"
                  height="24"
                  rx="4"
                  className="fill-muted/40 stroke-border"
                  strokeWidth="1"
                />
                <text x="40" y="166" className="fill-foreground font-mono text-[10px]">
                  ●●●●●●●●●●●●●●●●●●●●●●●●
                </text>
                <rect
                  x="390"
                  y="86"
                  width="340"
                  height="88"
                  rx="4"
                  className="fill-destructive/10 stroke-destructive/40"
                  strokeWidth="1"
                />
                <text x="402" y="110" className="fill-foreground text-[10px] font-semibold">
                  Shown once. Not retrievable later.
                </text>
                <text x="402" y="128" className="fill-muted-foreground text-[10px]">
                  Copy both values now, then store the
                </text>
                <text x="402" y="144" className="fill-muted-foreground text-[10px]">
                  secret in your secret manager.
                </text>
              </svg>
            </Figure>
          </Section>

          {/* 8 */}
          <Section id="endpoint" n={8} title="The S3 endpoint">
            <p>
              You do not type an endpoint anywhere. The driver constructs it from your Account ID:
            </p>
            <CodeBlock label="endpoint template — derived, not entered" code={ENDPOINT_TEMPLATE} />

            <Table
              head={['Part', 'Value', 'Where it comes from']}
              rows={[
                [
                  <Mono key="1">YOUR_ACCOUNT_ID</Mono>,
                  'Your Cloudflare account identifier',
                  'R2 overview page, the account home, or the endpoint shown with your API token',
                ],
                [
                  <Mono key="2">.r2.cloudflarestorage.com</Mono>,
                  'Fixed R2 S3-API host suffix',
                  'Constant — the driver appends it',
                ],
                [
                  <strong key="3">Bucket</strong>,
                  <Mono key="b">YOUR_BUCKET_NAME</Mono>,
                  'Sent as a separate parameter, not part of the endpoint. Because an endpoint is present, the driver uses path-style addressing, so requests look like the host above followed by /YOUR_BUCKET_NAME/<key>.',
                ],
                [
                  <strong key="4">Region</strong>,
                  <Mono key="r">auto</Mono>,
                  'Forced by the driver. R2 requires this exact literal for SigV4 signing; there is no form field.',
                ],
              ]}
            />

            <Callout tone="warning" title="Common endpoint mistakes">
              <ul className="ml-4 list-disc space-y-1">
                <li>
                  Entering the whole endpoint URL into the <strong>Account ID</strong> field. Enter
                  only the identifier, not <Mono>https://…</Mono>.
                </li>
                <li>
                  Using the public <Mono>r2.dev</Mono> development URL. That is a public delivery
                  host, not the S3 API, and it will not authenticate.
                </li>
                <li>Using a custom domain you attached for public delivery. Same problem.</li>
                <li>Including the bucket in the Account ID. The bucket is its own field.</li>
              </ul>
            </Callout>

            <Figure
              n={4}
              caption="How the request URL is assembled"
              look="You supply two of the three parts. The host suffix and the region are constants the driver adds."
            >
              <svg
                viewBox="0 0 820 150"
                className="h-auto w-full min-w-[640px]"
                role="img"
                aria-label="Diagram showing the endpoint assembled from account id plus the fixed r2.cloudflarestorage.com suffix, with bucket and key appended path-style."
              >
                <text x="20" y="40" className="fill-muted-foreground text-[10px]">
                  You enter
                </text>
                <rect
                  x="20"
                  y="50"
                  width="170"
                  height="34"
                  rx="5"
                  className="fill-primary/10 stroke-primary"
                  strokeWidth="1.4"
                />
                <text
                  x="105"
                  y="71"
                  textAnchor="middle"
                  className="fill-foreground font-mono text-[10px]"
                >
                  YOUR_ACCOUNT_ID
                </text>
                <text x="200" y="72" className="fill-muted-foreground font-mono text-[12px]">
                  +
                </text>
                <text x="222" y="40" className="fill-muted-foreground text-[10px]">
                  Driver constant
                </text>
                <rect
                  x="222"
                  y="50"
                  width="240"
                  height="34"
                  rx="5"
                  className="fill-muted/40 stroke-border"
                  strokeWidth="1.2"
                />
                <text
                  x="342"
                  y="71"
                  textAnchor="middle"
                  className="fill-foreground font-mono text-[10px]"
                >
                  .r2.cloudflarestorage.com
                </text>
                <text x="472" y="72" className="fill-muted-foreground font-mono text-[12px]">
                  /
                </text>
                <text x="492" y="40" className="fill-muted-foreground text-[10px]">
                  You enter
                </text>
                <rect
                  x="492"
                  y="50"
                  width="160"
                  height="34"
                  rx="5"
                  className="fill-primary/10 stroke-primary"
                  strokeWidth="1.4"
                />
                <text
                  x="572"
                  y="71"
                  textAnchor="middle"
                  className="fill-foreground font-mono text-[10px]"
                >
                  YOUR_BUCKET_NAME
                </text>
                <text x="662" y="72" className="fill-muted-foreground font-mono text-[12px]">
                  /
                </text>
                <rect
                  x="678"
                  y="50"
                  width="120"
                  height="34"
                  rx="5"
                  className="fill-muted/40 stroke-border"
                  strokeWidth="1.2"
                />
                <text
                  x="738"
                  y="71"
                  textAnchor="middle"
                  className="fill-foreground font-mono text-[10px]"
                >
                  &lt;object key&gt;
                </text>
                <text x="20" y="118" className="fill-muted-foreground text-[10px]">
                  Signed with AWS Signature V4, region
                </text>
                <rect
                  x="266"
                  y="104"
                  width="50"
                  height="20"
                  rx="4"
                  className="fill-muted/40 stroke-border"
                  strokeWidth="1"
                />
                <text
                  x="291"
                  y="118"
                  textAnchor="middle"
                  className="fill-foreground font-mono text-[10px]"
                >
                  auto
                </text>
              </svg>
            </Figure>
          </Section>

          {/* 9 */}
          <Section id="configure" n={9} title="Configure the provider in Service Portal">
            <p>
              Switch back to your first tab. On <strong>Storage Providers</strong>, find the{' '}
              <strong>Cloudflare R2</strong> card and select <strong>Configure</strong> (or{' '}
              <strong>Edit</strong>). A dialog opens with the four fields.
            </p>
            <Table
              head={[
                'Field',
                'What to enter',
                'Example placeholder',
                'Where to get it',
                'Common mistake',
              ]}
              rows={[
                [
                  <strong key="1">Account ID</strong>,
                  'The account identifier only — no protocol, no dots, no bucket',
                  <Mono key="e1">YOUR_ACCOUNT_ID</Mono>,
                  'R2 overview page / account home',
                  'Pasting the whole https://… endpoint',
                ],
                [
                  <strong key="2">Bucket Name</strong>,
                  'The bucket name exactly as created',
                  <Mono key="e2">YOUR_BUCKET_NAME</Mono>,
                  'R2 → Buckets',
                  'Adding s3://, a trailing slash, or a folder path',
                ],
                [
                  <strong key="3">Access Key ID</strong>,
                  'From the R2 API token',
                  <Mono key="e3">YOUR_ACCESS_KEY_ID</Mono>,
                  'R2 → Manage R2 API Tokens',
                  'Using a Cloudflare API token or global API key instead',
                ],
                [
                  <strong key="4">Secret Access Key</strong>,
                  'From the same token — shown once',
                  <em key="e4">(never displayed again)</em>,
                  'Your secret manager',
                  'A truncated paste, or trailing whitespace',
                ],
              ]}
            />

            <Callout tone="security" title="The blank-secret rule">
              <p>
                Reopening the dialog on an already-configured provider shows the secret fields{' '}
                <strong>empty</strong>, with the hint{' '}
                <em>&ldquo;Leave blank to keep the current value.&rdquo;</em> That is deliberate:
                stored secrets are masked when read back, and submitting the mask would overwrite
                your real credential with the mask characters. Leave a secret field blank unless you
                are genuinely replacing it.
              </p>
            </Callout>

            <Figure
              n={5}
              caption="Service Portal → Storage Providers → Cloudflare R2 → Configure"
              look="Four fields, all required. No Region box and no Custom Endpoint box — that is correct for R2."
            >
              <svg
                viewBox="0 0 620 290"
                className="h-auto w-full min-w-[520px]"
                role="img"
                aria-label="Wireframe of the Configure Cloudflare R2 dialog with Account ID, Bucket Name, Access Key ID and Secret Access Key fields."
              >
                <rect
                  x="10"
                  y="10"
                  width="600"
                  height="270"
                  rx="10"
                  className="fill-background stroke-border"
                  strokeWidth="1.5"
                />
                <text x="30" y="40" className="fill-foreground text-[13px] font-semibold">
                  Configure Cloudflare R2
                </text>
                <rect
                  x="30"
                  y="52"
                  width="200"
                  height="24"
                  rx="5"
                  className="fill-primary/5 stroke-primary/40"
                  strokeWidth="1"
                />
                <text x="42" y="68" className="fill-primary text-[10px]">
                  📖 R2 Setup Guide (new tab)
                </text>
                {[
                  ['Account ID *', 'YOUR_ACCOUNT_ID'],
                  ['Bucket Name *', 'YOUR_BUCKET_NAME'],
                  ['Access Key ID *', '••••••••'],
                  ['Secret Access Key *', '••••••••'],
                ].map(([label, val], i) => (
                  <g key={label}>
                    <text
                      x="30"
                      y={104 + i * 44}
                      className="fill-muted-foreground text-[10px] font-medium"
                    >
                      {label}
                    </text>
                    <rect
                      x="30"
                      y={112 + i * 44}
                      width="550"
                      height="26"
                      rx="4"
                      className="fill-muted/40 stroke-border"
                      strokeWidth="1"
                    />
                    <text x="40" y={129 + i * 44} className="fill-foreground font-mono text-[10px]">
                      {val}
                    </text>
                  </g>
                ))}
              </svg>
            </Figure>

            <p>
              Select <strong>Save</strong>. The two credential fields are encrypted at rest. Saving
              does not move any file and does not make R2 the active provider.
            </p>
          </Section>

          {/* 10 */}
          <Section id="connection-test" n={10} title="Connection test">
            <p>
              On the Cloudflare R2 card, select <strong>Run Connection Test</strong>.
            </p>
            <Callout tone="warning" title="What the test checks — and what it does not">
              <p>
                The test issues a single <Mono>HeadBucket</Mono> call. It proves the account
                endpoint resolves, the bucket exists and is addressable, the credentials are valid
                and correctly signed, and the token can reach that bucket.
              </p>
              <p>
                It does <strong>not</strong> upload, download, list or delete anything. So it does{' '}
                <strong>not</strong> prove that writes, reads or deletes will succeed. A token with
                the wrong permission level can pass this test and still fail on first upload — which
                is exactly why section 11 exists.
              </p>
            </Callout>
            <p>On success the provider reports healthy and returns these non-secret coordinates:</p>
            <Table
              head={['Field', 'Expected for R2']}
              rows={[
                [<Mono key="1">driverId</Mono>, <Mono key="v1">r2</Mono>],
                [
                  <Mono key="2">region</Mono>,
                  <span key="v2">
                    <Mono>auto</Mono> — the forced literal
                  </span>,
                ],
                [<Mono key="3">bucket</Mono>, 'The bucket that answered'],
                [
                  <Mono key="4">endpoint</Mono>,
                  <span key="v4">
                    <Mono>https://&lt;accountId&gt;.r2.cloudflarestorage.com</Mono> — confirms the
                    Account ID was read correctly
                  </span>,
                ],
                [
                  <Mono key="5">forcePathStyle</Mono>,
                  <span key="v5">
                    <Mono>true</Mono> — expected, because an endpoint is present
                  </span>,
                ],
              ]}
            />
            <p>
              Credentials are never included in the result. Latency is recorded, and the
              provider&rsquo;s health status and last-checked timestamp are updated.
            </p>
            <Callout tone="tip">
              <p>
                The returned <Mono>endpoint</Mono> is the quickest way to catch a mistyped Account
                ID: if it does not match the endpoint Cloudflare showed you, the Account ID field is
                wrong.
              </p>
            </Callout>
          </Section>

          {/* 11 */}
          <Section id="verify-ops" n={11} title="Verify real storage operations">
            <p>
              Because the connection test only covers <Mono>HeadBucket</Mono>, verify the operations
              that matter — safely, and never against existing production data.
            </p>
            <Table
              head={['Operation', 'Safe way to verify', 'If it fails']}
              rows={[
                [
                  <strong key="1">Write</strong>,
                  'Upload one small, disposable test file through the application to a non-production tenant or a scratch record. Do not hand-place objects in the bucket outside the application — the catalogue would not know about them.',
                  'Token lacks write — it is probably Object Read only (section 6)',
                ],
                [
                  <strong key="2">Read / download</strong>,
                  'Download that same test file through the application and confirm the bytes open correctly',
                  'Token lacks read, or the object key is wrong',
                ],
                [
                  <strong key="3">Signed URL</strong>,
                  'Generate a signed URL for the test file and open it in a browser',
                  'A 403 usually means read permission, or an expired URL — regenerate rather than reusing an old link',
                ],
                [
                  <strong key="4">Listing</strong>,
                  'Open the provider’s storage usage panel, which performs a full paginated listing and reports object count and total bytes',
                  'Token cannot list objects in this bucket',
                ],
                [
                  <strong key="5">Delete</strong>,
                  'Delete ONLY the test asset you just created, through the application',
                  'Token lacks delete — uploads will work but lifecycle operations will not',
                ],
              ]}
            />
            <Callout tone="warning" title="Never test by deleting real data">
              <p>
                Do not delete an existing asset to &ldquo;check delete works&rdquo;, and do not
                remove objects directly in the Cloudflare dashboard. The asset catalogue tracks what
                should exist; deleting underneath it creates an orphaned record that surfaces later
                as a missing object. Create a disposable test asset, verify against that, and remove
                only that.
              </p>
            </Callout>
          </Section>

          {/* 12 */}
          <Section id="migration" n={12} title="R2 and storage migration">
            <p>
              Once R2 is configured and verified you may migrate existing assets into it. A provider
              migration copies every object owned by a <strong>source</strong> provider to a{' '}
              <strong>target</strong>, verifies each copy, moves catalogue{' '}
              <strong>ownership</strong>, and only then records completion. R2 can be either side —
              target when adopting R2, source when moving away from it.
            </p>

            <Callout tone="tip" title="The source is never deleted">
              <p>
                The engine <strong>never</strong> deletes a source object. After a successful
                migration the bytes still exist on the old provider, and one of the finalization
                gates checks exactly that. R2 needs no extra permission for migration — the same
                object read/write/list operations from section 6 cover it.
              </p>
            </Callout>

            <Table
              head={['Status', 'Meaning']}
              rows={[
                [<Mono key="1">PENDING</Mono>, 'Planned, nothing executed'],
                [<Mono key="2">DRY_RUN</Mono>, 'Rehearsal in progress — writes nothing'],
                [
                  <Mono key="3">DRY_RUN_COMPLETE</Mono>,
                  'Rehearsal finished; this is where you approve',
                ],
                [<Mono key="4">READY</Mono>, 'Approved, not yet copying'],
                [<Mono key="5">RUNNING</Mono>, 'Objects being copied to the target'],
                [<Mono key="6">PAUSED</Mono>, 'Copying suspended; resumable'],
                [
                  <Mono key="7">VERIFYING</Mono>,
                  'All copying done. Awaiting ownership flip and finalization — a stable resting state; nothing happens on its own',
                ],
                [<Mono key="8">COMPLETED</Mono>, 'Finalized, all twenty gates passed. Terminal'],
                [<Mono key="9">FAILED</Mono>, 'Terminal'],
                [<Mono key="10">ROLLED_BACK</Mono>, 'Ownership returned to the source. Terminal'],
                [
                  <Mono key="11">SOURCE_IRRECOVERABLE</Mono>,
                  'Destination copy verified, but the source objects are permanently gone, so finalization can never pass. Neither success nor failure. Terminal',
                ],
              ]}
            />

            <Callout tone="note" title="Why SOURCE_IRRECOVERABLE matters for lifecycle">
              <p>
                A migration whose source has been lost has not satisfied source retention, so
                calling it COMPLETED would be untrue — but the data did arrive, so it is not a
                failure either. It has its own terminal state, and that state is deliberately{' '}
                <strong>not</strong> treated as &ldquo;finished&rdquo; by the asset lifecycle, so
                the affected assets remain protected from purge.
              </p>
            </Callout>

            <p>
              Verification before finalization is thorough: every item must be COMPLETED and
              verified, source and target checksums must agree, every target object must be present
              and must <strong>re-hash</strong> to its recorded SHA-256, ownership must have moved
              to the target, and the source objects must still exist. Finalization is irreversible
              and runs all twenty gates again, refusing at the first failure.
            </p>

            <Figure
              n={6}
              caption="Migration — source to target, then verification"
              look="Copy and verify happen before ownership moves, and finalization is a separate explicit step. The source copy is retained throughout."
            >
              <svg
                viewBox="0 0 860 170"
                className="h-auto w-full min-w-[680px]"
                role="img"
                aria-label="Diagram: source provider copies objects to target provider, checksums are verified, ownership flips, then finalization runs twenty gates. Source objects are retained."
              >
                <rect
                  x="10"
                  y="45"
                  width="150"
                  height="60"
                  rx="8"
                  className="fill-background stroke-border"
                  strokeWidth="1.5"
                />
                <text x="85" y="70" textAnchor="middle" className="fill-foreground text-[11px]">
                  Source provider
                </text>
                <text
                  x="85"
                  y="88"
                  textAnchor="middle"
                  className="fill-muted-foreground text-[9px]"
                >
                  objects RETAINED
                </text>
                <line
                  x1="162"
                  y1="75"
                  x2="228"
                  y2="75"
                  className="stroke-muted-foreground"
                  strokeWidth="1.5"
                  markerEnd="url(#mArrow)"
                />
                <text
                  x="195"
                  y="67"
                  textAnchor="middle"
                  className="fill-muted-foreground text-[9px]"
                >
                  copy
                </text>
                <rect
                  x="230"
                  y="45"
                  width="150"
                  height="60"
                  rx="8"
                  className="fill-primary/10 stroke-primary"
                  strokeWidth="1.5"
                />
                <text x="305" y="70" textAnchor="middle" className="fill-foreground text-[11px]">
                  Target provider
                </text>
                <text
                  x="305"
                  y="88"
                  textAnchor="middle"
                  className="fill-muted-foreground text-[9px]"
                >
                  e.g. Cloudflare R2
                </text>
                <line
                  x1="382"
                  y1="75"
                  x2="448"
                  y2="75"
                  className="stroke-muted-foreground"
                  strokeWidth="1.5"
                  markerEnd="url(#mArrow)"
                />
                <rect
                  x="450"
                  y="45"
                  width="160"
                  height="60"
                  rx="8"
                  className="fill-background stroke-border"
                  strokeWidth="1.5"
                />
                <text x="530" y="70" textAnchor="middle" className="fill-foreground text-[11px]">
                  Verify
                </text>
                <text
                  x="530"
                  y="88"
                  textAnchor="middle"
                  className="fill-muted-foreground text-[9px]"
                >
                  re-hash SHA-256
                </text>
                <line
                  x1="612"
                  y1="75"
                  x2="678"
                  y2="75"
                  className="stroke-muted-foreground"
                  strokeWidth="1.5"
                  markerEnd="url(#mArrow)"
                />
                <rect
                  x="680"
                  y="45"
                  width="170"
                  height="60"
                  rx="8"
                  className="fill-background stroke-border"
                  strokeWidth="1.5"
                />
                <text x="765" y="70" textAnchor="middle" className="fill-foreground text-[11px]">
                  Ownership → Finalize
                </text>
                <text
                  x="765"
                  y="88"
                  textAnchor="middle"
                  className="fill-muted-foreground text-[9px]"
                >
                  20 gates, irreversible
                </text>
                <text x="10" y="140" className="fill-muted-foreground text-[9px]">
                  Source objects are never deleted by the migration — gate 17 checks they are still
                  present.
                </text>
                <defs>
                  <marker
                    id="mArrow"
                    markerWidth="8"
                    markerHeight="8"
                    refX="7"
                    refY="4"
                    orient="auto"
                  >
                    <path d="M0,0 L8,4 L0,8 z" className="fill-muted-foreground" />
                  </marker>
                </defs>
              </svg>
            </Figure>
          </Section>

          {/* 13 */}
          <Section id="readiness" n={13} title="Migration readiness checklist">
            <Checklist
              items={[
                'R2 bucket created and visible in the Cloudflare dashboard',
                'Bucket is private — public access and r2.dev public URL are disabled',
                'No R2 object lifecycle/expiry rule exists on the bucket',
                'R2 API token created with Object Read & Write, scoped to this bucket only',
                'Token is NOT a Cloudflare API token and NOT a global API key',
                'Access Key ID and Secret Access Key stored in a secret manager',
                'Account ID recorded — the identifier only, not the endpoint URL',
                'Service Portal R2 provider configured — all four fields',
                'Connection test passes (remembering it only proves HeadBucket)',
                'Test upload, download, signed URL, listing and delete all verified with a disposable asset',
                'Returned endpoint matches the account you expect',
                'Source provider healthy and reachable',
                'Database and file backup completed and verified',
                'Dry run completed with no blocking findings',
                'You hold platform:system:manage',
                'You understand the source is retained and that finalization is irreversible',
              ]}
            />
          </Section>

          {/* 14 */}
          <Section id="troubleshooting" n={14} title="Troubleshooting">
            <p>
              Driver errors are wrapped and surfaced with a <Mono>Provider error [driver=r2]</Mono>{' '}
              prefix; the underlying message follows it. Configuration problems are reported before
              any network call as{' '}
              <Mono>Provider is not fully configured. Missing required keys: …</Mono>.
            </p>
            <Table
              head={['Symptom', 'Likely cause', 'Check', 'Correction']}
              rows={[
                [
                  <Mono key="1">Missing required keys: accountId</Mono>,
                  'Account ID empty, or the endpoint could not be derived',
                  'Reopen Configure',
                  'Enter the account identifier only',
                ],
                [
                  <Mono key="2">
                    Missing required keys: bucket / accessKeyId / secretAccessKey
                  </Mono>,
                  'A required field has no stored value',
                  'Reopen Configure — the message names the key',
                  'Complete the named field',
                ],
                [
                  <Mono key="3">AccessDenied</Mono>,
                  'Token permission too low, or scoped to a different bucket',
                  'R2 → Manage R2 API Tokens: check the permission level and bucket scope',
                  'Reissue with Object Read & Write scoped to this bucket. Never make the bucket public.',
                ],
                [
                  <Mono key="4">InvalidAccessKeyId</Mono>,
                  'Access Key ID wrong, or the token was deleted/revoked',
                  'Compare against the token record',
                  'Re-enter, or create a new token',
                ],
                [
                  <Mono key="5">SignatureDoesNotMatch</Mono>,
                  'Secret truncated, has trailing whitespace, or belongs to a different token than the Access Key ID',
                  'Confirm both halves came from the SAME token',
                  'Re-enter the secret; if lost, create a new token',
                ],
                [
                  'Credential mismatch (key from one token, secret from another)',
                  'Two tokens were created and the values were mixed',
                  'The pair must originate from one creation event',
                  'Rotate to a single fresh token (section 16)',
                ],
                [
                  <Mono key="6">NoSuchBucket</Mono>,
                  'Bucket name wrong, or the bucket lives in a different account',
                  'R2 → Buckets, compare character for character',
                  'Enter the bare bucket name; confirm the Account ID matches the account holding it',
                ],
                [
                  'Wrong account ID',
                  'The endpoint resolves to a different account',
                  'Run the connection test and read the returned endpoint',
                  'Correct the Account ID so the endpoint matches Cloudflare’s',
                ],
                [
                  'Wrong endpoint entered somewhere',
                  'An r2.dev URL, a custom domain, or a full https:// URL was pasted into Account ID',
                  'Account ID must be an identifier, not a URL',
                  'Enter the identifier alone; the driver builds the endpoint',
                ],
                [
                  'Incorrect region',
                  'Not possible through the form — the driver forces auto',
                  'If a signing error mentions region, suspect a stale cached config',
                  'Re-save the configuration so the driver re-reads it',
                ],
                [
                  'Connection test succeeds but upload fails',
                  'Token is Object Read only — HeadBucket passes, PutObject does not',
                  'Check the token permission level',
                  'Reissue with Object Read & Write',
                ],
                [
                  'Connection test succeeds but listing fails',
                  'Token cannot list objects in this bucket, or the scope is wrong',
                  'Open the provider usage panel and read the error',
                  'Reissue scoped to this bucket with Object Read & Write',
                ],
                [
                  'Expired or revoked credentials',
                  'The token had a TTL, or was deleted in the dashboard',
                  'R2 → Manage R2 API Tokens: is the token still listed and valid?',
                  'Create a new token and rotate (section 16)',
                ],
                [
                  <Mono key="7">ECONNREFUSED / ENOTFOUND / getaddrinfo</Mono>,
                  'DNS or egress blocked from the backend host, or a malformed Account ID produced an unresolvable host',
                  'Resolve the endpoint host from the backend network',
                  'Fix egress/DNS; correct the Account ID',
                ],
                [
                  'Timeouts / intermittent failures',
                  'Network path or a very large object',
                  'Check backend network health and object size',
                  'Retry; investigate egress if persistent',
                ],
                [
                  'CORS errors in a browser',
                  'Not applicable to this application',
                  'Service Portal fetches objects server-side and issues signed URLs; it makes no cross-origin XHR to R2',
                  'If you see one, something outside this application is calling R2 directly — do not add a CORS policy to “fix” Service Portal',
                ],
                [
                  'Provider shows OFFLINE',
                  'A connection test failed, or configuration is incomplete',
                  'The card shows the last health error',
                  'Resolve the error, then re-run the connection test',
                ],
              ]}
            />
          </Section>

          {/* 15 */}
          <Section id="security" n={15} title="Security best practices">
            <ul className="ml-5 list-disc space-y-2">
              <li>
                <strong>Least privilege.</strong> Object Read &amp; Write, scoped to one bucket.
                Never Admin, never account-wide.
              </li>
              <li>
                <strong>Private bucket, always.</strong> No public access, no r2.dev public URL.
                Signed URLs are the access path.
              </li>
              <li>
                <strong>Never the global API key.</strong> It carries full account access and is
                tied to your login. It does not work here anyway.
              </li>
              <li>
                <strong>Secret hygiene.</strong> Never in source code, never in frontend code, never
                in a committed <Mono>.env</Mono>, never in a screenshot, log, report or support
                ticket.
              </li>
              <li>
                <strong>Separate credentials per environment.</strong> Production and staging must
                never share a token or a bucket.
              </li>
              <li>
                <strong>Rotate on a schedule</strong>, and diarise any TTL you set.
              </li>
              <li>
                <strong>Protect the Service Portal configuration itself.</strong> Editing a provider
                requires <Mono>platform:system:manage</Mono> — treat that permission as
                production-critical and grant it sparingly.
              </li>
              <li>
                <strong>If credentials are exposed:</strong> treat them as compromised immediately.
                Create a replacement token, update the provider, verify, then{' '}
                <strong>delete the exposed token</strong> in Cloudflare. Then review R2 access logs
                and rotate anything else that shared the exposure.
              </li>
              <li>
                <strong>Never widen permissions to clear an error.</strong> Diagnose which operation
                was denied and grant precisely that.
              </li>
              <li>
                <strong>Never delete source data</strong> because a migration looks successful. The
                asset lifecycle decides that, not you and not an R2 lifecycle rule.
              </li>
            </ul>
          </Section>

          {/* 16 */}
          <Section id="rotation" n={16} title="Credential rotation">
            <p>
              Rotate without downtime by overlapping the two tokens. Never delete the old one first.
            </p>
            <ol className="ml-5 list-decimal space-y-2">
              <li>
                <strong>Create</strong> a new R2 API token — Object Read &amp; Write, scoped to the
                same bucket. Copy both values.
              </li>
              <li>
                <strong>Update</strong> the provider: Configure → replace Access Key ID and Secret
                Access Key. Both must come from the <em>same</em> new token.
              </li>
              <li>
                <strong>Test</strong>: run the connection test. It should pass and return the same
                endpoint and bucket.
              </li>
              <li>
                <strong>Verify</strong> real operations — upload, download, signed URL, listing —
                using a disposable test asset (section 11). The connection test alone is not enough.
              </li>
              <li>
                <strong>Observe</strong> for a short period to be sure nothing is still failing.
              </li>
              <li>
                <strong>Revoke</strong>: only now delete the old token in Cloudflare.
              </li>
            </ol>
            <Callout tone="tip" title="Rollback">
              <p>
                If step 3 or 4 fails, re-enter the <em>old</em> credentials and save — the old token
                is still valid because you have not deleted it. That is the entire reason revocation
                is last. Once the old token is deleted there is no rollback, so do not compress
                these steps.
              </p>
            </Callout>
          </Section>

          {/* 17 */}
          <Section id="operational" n={17} title="Operational notes">
            <Table
              head={['Topic', 'What to know']}
              rows={[
                [
                  'S3 compatibility',
                  'The driver uses only PutObject, GetObject, HeadObject, DeleteObject, CopyObject, ListObjectsV2, HeadBucket and presigned GET — all well-supported by R2. It sends no ACL, no storage class and no server-side-encryption parameters.',
                ],
                [
                  'Addressing',
                  'Because an endpoint is always present for R2, the driver uses path-style addressing. This is expected and correct; forcePathStyle=true in the connection test result is not a misconfiguration.',
                ],
                [
                  'Region',
                  'Always the literal auto. It is never asked for and cannot be changed from the form.',
                ],
                [
                  'Object naming',
                  'Keys are generated by the application as tenant/<tenantId>/<module>/… at the bucket root. Do not rename or reorganise objects in the Cloudflare dashboard — the catalogue tracks keys and would no longer find them.',
                ],
                [
                  'Versioning',
                  'The driver is version-unaware: it never sends a VersionId and never lists versions. Do not rely on object-version recovery as a backup strategy for this application.',
                ],
                [
                  'Listing and usage figures',
                  'Provider usage performs a full paginated ListObjectsV2 over the whole bucket. Keep the bucket dedicated to Service Portal so those figures stay meaningful.',
                ],
                [
                  'Physical vs logical figures',
                  'Physical counts objects in the bucket; logical counts catalogue rows by ownership. After a migration these legitimately diverge, because the source objects are retained by design.',
                ],
                [
                  'Configuration caching',
                  'The driver caches resolved config and its client, and invalidates on save. If behaviour looks stale after an out-of-band change, re-save the provider configuration.',
                ],
                [
                  'Egress',
                  'R2 does not charge egress, but Class A/B operation counts still apply. A full migration performs a read and a write per object, plus a re-hash read during verification.',
                ],
              ]}
            />
          </Section>

          {/* 18 */}
          <Section id="quick-start" n={18} title="Quick start checklist">
            <Checklist
              items={[
                '1. Sign in to Cloudflare and open R2; enable it if this is the first use',
                '2. Note the Account ID from the R2 overview',
                '3. Create a bucket — lowercase name with the environment in it, Standard class, public access left OFF',
                '4. R2 → Manage R2 API Tokens → Create API token',
                '5. Permission: Object Read & Write; scope: this bucket only',
                '6. Copy the Access Key ID and the Secret Access Key — the secret is shown once',
                '7. Store both in your secret manager',
                '8. Service Portal → Storage Providers → Cloudflare R2 → Configure',
                '9. Enter Account ID, Bucket Name, Access Key ID, Secret Access Key (there is no Region or Endpoint field)',
                '10. Save, then Run Connection Test',
                '11. Confirm the returned endpoint and bucket are the ones you expect',
                '12. Verify upload, download, signed URL, listing and delete with one disposable test asset',
                '13. Only then consider making R2 active or migrating assets into it',
              ]}
            />
          </Section>

          <div className="border-t border-border pt-6">
            <Link
              to={PLATFORM_ROUTES.STORAGE_PROVIDERS}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
              Back to Storage Providers
            </Link>
            <p className="mt-3 text-xs text-muted-foreground">
              If you opened this guide from the Configure dialog, your original tab is still open
              with your form intact — switch back to it rather than using this link.
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
